import { useState, useEffect, useCallback, useRef } from 'react';
import { SessionService, isRetryableError, isRetryableStatus } from '../services/sessionService';
import { ScanQueue, QueuedScan } from '../services/scanQueue';
import { flushScanQueue, rejectionMessage } from '../services/scanSync';
import { getQueryParam, isValidUUID } from '../utils/urlUtils';
import { Session } from '../api/types';
import { API_URLS } from '../config/api';

/** Shown while scans wait to sync, when the server refused one for good, or
 *  when some other step of the crawl couldn't reach the server. */
export type SyncNotice =
  | { kind: 'pending' }
  | { kind: 'failed'; message: string; scan: QueuedScan }
  | { kind: 'error'; message: string; retry?: () => Promise<void> };

const SESSION_LOAD_FAILED = "Couldn't load your crawl. Check your signal and try again.";

/** Automatic retry backoff for queued scans: 2s, 4s, 8s… capped at a minute.
 *  Coming back online, returning to the tab and app load retry immediately. */
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 60_000;

/**
 * Owns every behaviour-critical piece of session orchestration: QR-scan entry,
 * resume-link adoption, scan recording, completion detection, and the
 * cross-crawl abandon flow. Moved verbatim out of App.tsx — the logic here is
 * load-bearing and must not change as part of visual work.
 */
export const useSessionOrchestration = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showCompletionPopup, setShowCompletionPopup] = useState(false);
  const [completedSession, setCompletedSession] = useState<Session | null>(null);
  const [completionPopupDismissed, setCompletionPopupDismissed] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [showAbandonPopup, setShowAbandonPopup] = useState(false);
  const [abandonPopupData, setAbandonPopupData] = useState<{
    currentCrawlName: string;
    newPubName: string;
  } | null>(null);
  /** The very first scan couldn't start a crawl (no signal): there is no
   *  session to queue it against, so it blocks with a retry instead. */
  const [showStartFailed, setShowStartFailed] = useState(false);
  /** Pubs on the current crawl scanned but not yet confirmed by the server */
  const [pendingPubIds, setPendingPubIds] = useState<string[]>(
    () => ScanQueue.forSession(SessionService.getSessionId()).map(scan => scan.pubId)
  );
  const [syncNotice, setSyncNotice] = useState<SyncNotice | null>(null);

  /** The completion sheet shows once per session per page load, however many
   *  syncs report the session complete. */
  const completionShownFor = useRef<string | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryAttempt = useRef(0);
  /** The stored crawl couldn't be fetched (no signal): try again on reconnect */
  const sessionLoadFailed = useRef(false);

  /** A scan the server confirmed: show it, and celebrate if it was the last. */
  const applyScannedSession = useCallback((updatedSession: Session) => {
    sessionLoadFailed.current = false;
    setCurrentSession(updatedSession);
    if (updatedSession.isComplete && completionShownFor.current !== updatedSession.id) {
      completionShownFor.current = updatedSession.id;
      setCompletedSession(updatedSession);
      setShowCompletionPopup(true);
    }
    setRefreshTrigger(prev => prev + 1);
  }, []);

  /**
   * Push queued scans to the server and reflect the result: confirmed scans
   * update the crawl, refused ones raise an error banner, and anything still
   * queued stays marked pending with a backoff retry scheduled.
   */
  const syncScans = useCallback(async () => {
    clearTimeout(retryTimer.current);
    const { outcomes, blocked } = await flushScanQueue();
    const activeId = SessionService.getSessionId();

    let latest: Session | null = null;
    for (const outcome of outcomes) {
      if (outcome.scan.sessionId !== activeId) continue;
      if (outcome.kind === 'synced') {
        latest = outcome.session;
      } else {
        setSyncNotice({
          kind: 'failed',
          message: rejectionMessage(outcome.error, outcome.scan.pubName),
          scan: outcome.scan,
        });
      }
    }
    if (latest) {
      applyScannedSession(latest);
    }

    const pending = ScanQueue.forSession(activeId);
    setPendingPubIds(pending.map(scan => scan.pubId));
    if (blocked && ScanQueue.all().length > 0) {
      if (pending.length > 0) {
        setSyncNotice(notice => (notice && notice.kind !== 'pending' ? notice : { kind: 'pending' }));
      }
      const delay = Math.min(RETRY_BASE_MS * 2 ** retryAttempt.current, RETRY_MAX_MS);
      retryAttempt.current += 1;
      retryTimer.current = setTimeout(() => { void syncScans(); }, delay);
    } else {
      retryAttempt.current = 0;
      setSyncNotice(notice => (notice?.kind === 'pending' ? null : notice));
    }
  }, [applyScannedSession]);

  /** Queue a scan (stamped with the real scan time) and try to send it. The
   *  session id is enough: the session itself may not have loaded. */
  const submitScan = useCallback(async (sessionId: string, pubId: string, pubName?: string) => {
    ScanQueue.enqueue({
      sessionId,
      pubId,
      scannedAt: new Date().toISOString(),
      pubName,
    });
    await syncScans();
  }, [syncScans]);

  /** Fetch the stored crawl again after a load failed for lack of signal. */
  const reloadSession = useCallback(async () => {
    const sessionId = SessionService.getSessionId();
    if (!sessionId) return;
    try {
      const session = await SessionService.getSession(sessionId);
      sessionLoadFailed.current = false;
      setCurrentSession(session);
      setSyncNotice(notice => (notice?.kind === 'error' ? null : notice));
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error reloading session:', error);
      if (isRetryableError(error)) {
        sessionLoadFailed.current = true;
        setSyncNotice({ kind: 'error', message: SESSION_LOAD_FAILED, retry: reloadSession });
      } else {
        // Gone for good: MyCrawl shows the expired or missing crawl
        sessionLoadFailed.current = false;
        setSyncNotice(notice => (notice?.kind === 'error' ? null : notice));
      }
    }
  }, []);

  /**
   * Start a session for the given pub. Auto-starts when the pub is on exactly
   * one crawl, otherwise shows the crawl chooser.
   */
  const startSessionForPub = useCallback(async (pubId: string) => {
    try {
      const response = await fetch(API_URLS.PUB(pubId));
      if (!response.ok) {
        if (isRetryableStatus(response.status)) {
          setShowStartFailed(true);
        } else {
          setShowStartFailed(false);
          setShowWelcomePopup(true);
        }
        return;
      }
      const pubData = await response.json();
      if (pubData.crawls && pubData.crawls.length === 1) {
        const session = await SessionService.createSession(pubData.crawls[0].id);
        SessionService.setSessionId(session.id);
        // From here the crawl exists, so the scan itself can queue if needed
        setShowStartFailed(false);
        setCurrentSession(session);
        await submitScan(session.id, pubId, session.pubs.find(pub => pub.id === pubId)?.name);
        setRefreshTrigger(prev => prev + 1);
      } else {
        setShowStartFailed(false);
        setShowWelcomePopup(true);
      }
    } catch (error) {
      console.error('Error starting session for pub:', error);
      if (isRetryableError(error)) {
        setShowStartFailed(true);
      } else {
        setShowStartFailed(false);
        setShowWelcomePopup(true);
      }
    }
  }, [submitScan]);

  /**
   * A scanned pub isn't on the current crawl: look it up and offer to submit
   * the current crawl and start the pub's one.
   */
  const offerAbandon = useCallback(async (session: Session, pubId: string) => {
    const lookupFailed = () => setSyncNotice({
      kind: 'error',
      message: "Couldn't look up the pub you scanned. Check your signal and try again.",
      retry: () => offerAbandon(session, pubId),
    });
    try {
      const pubResponse = await fetch(API_URLS.PUB(pubId));
      if (!pubResponse.ok) {
        if (isRetryableStatus(pubResponse.status)) {
          lookupFailed();
        } else {
          setSyncNotice({ kind: 'error', message: "That QR code isn't for a pub we know." });
        }
        return;
      }
      const pubData = await pubResponse.json();
      setSyncNotice(notice => (notice?.kind === 'error' ? null : notice));
      setAbandonPopupData({
        currentCrawlName: session.crawlName || 'Current Crawl',
        newPubName: pubData.name || 'This Pub'
      });
      setShowAbandonPopup(true);
    } catch (error) {
      console.error('Error fetching pub data for abandon popup:', error);
      lookupFailed();
    }
  }, []);

  /**
   * Record a scan against the active session, handling completion and
   * scans of pubs that belong to a different crawl.
   */
  const recordScan = useCallback(async (session: Session, pubId: string) => {
    const sessionPub = session.pubs.find(pub => pub.id === pubId);

    if (!sessionPub) {
      await offerAbandon(session, pubId);
      return;
    }

    if (sessionPub.isComplete) {
      // Re-scan of an already-scanned pub: silent no-op
      return;
    }

    await submitScan(session.id, pubId, sessionPub.name);
  }, [offerAbandon, submitScan]);

  /** Resume link: adopt the referenced session onto this device */
  const adoptSession = useCallback(async (sessionParam: string) => {
    try {
      const adopted = await SessionService.getSession(sessionParam);
      const localSessionId = SessionService.getSessionId();
      let adopt = true;
      if (localSessionId && localSessionId !== adopted.id) {
        const localIsLive = await SessionService.validateSession(localSessionId);
        if (localIsLive) {
          adopt = window.confirm(
            'This device already has a crawl in progress. Open the shared crawl instead?'
          );
        }
      }
      setSyncNotice(notice => (notice?.kind === 'error' ? null : notice));
      if (adopt) {
        SessionService.setSessionId(adopted.id);
        setCurrentSession(adopted);
        setRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error adopting session from resume link:', error);
      setSyncNotice(isRetryableError(error)
        ? {
            kind: 'error',
            message: "Couldn't open the shared crawl. Check your signal and try again.",
            retry: () => adoptSession(sessionParam),
          }
        : { kind: 'error', message: "That crawl link doesn't work: the crawl has expired or doesn't exist." });
    }
  }, []);

  const initialise = useCallback(async () => {
    const pubId = getQueryParam('pubId');
    const sessionParam = getQueryParam('session');

    if (sessionParam && isValidUUID(sessionParam)) {
      await adoptSession(sessionParam);
      setIsLoading(false);
      void syncScans();
      return;
    }

    const sessionId = SessionService.getSessionId();
    let session: Session | null = null;
    let sessionUnreachable = false;

    if (sessionId) {
      try {
        session = await SessionService.getSession(sessionId);
      } catch (error) {
        console.error('Error checking session:', error);
        // Only a definite answer (e.g. 404) means the crawl is gone
        sessionUnreachable = isRetryableError(error);
      }
    }

    if (sessionId && sessionUnreachable) {
      // No signal to check the stored crawl. It's still the player's crawl, so
      // never start a new one here: that would silently leave theirs behind.
      sessionLoadFailed.current = true;
      if (pubId && isValidUUID(pubId)) {
        // Queue the scan against it; syncing it loads the crawl. If the pub
        // isn't on this crawl, the server refuses it and the banner says so.
        await submitScan(sessionId, pubId);
      } else {
        setSyncNotice({ kind: 'error', message: SESSION_LOAD_FAILED, retry: reloadSession });
      }
    } else if (session && pubId && isValidUUID(pubId)) {
      if (session.isFinalised || session.isComplete) {
        // Scan after finalisation: a fresh session begins for that pub's crawl
        await startSessionForPub(pubId);
      } else {
        setCurrentSession(session);
        await recordScan(session, pubId);
      }
    } else if (session) {
      setCurrentSession(session);
      // Reopening after completion (e.g. page refresh): show the celebration once
      if (session.isComplete && !completionPopupDismissed) {
        completionShownFor.current = session.id;
        setCompletedSession(session);
        setShowCompletionPopup(true);
      }
    } else if (pubId && isValidUUID(pubId)) {
      await startSessionForPub(pubId);
    }

    setIsLoading(false);
    // Scans left queued by an earlier visit retry on every app load (a scan
    // load has already flushed the queue when it sent its own scan)
    if (!(pubId && isValidUUID(pubId))) {
      void syncScans();
    }
    // completionPopupDismissed intentionally omitted: initialise must run once
    // per page load, not re-fire when the popup closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adoptSession, recordScan, reloadSession, startSessionForPub, submitScan, syncScans]);

  useEffect(() => {
    initialise();
  }, [initialise]);

  // Retry queued scans the moment the connection or the player comes back
  useEffect(() => {
    const retry = () => {
      void syncScans();
      if (sessionLoadFailed.current) void reloadSession();
    };
    const handleOnline = () => retry();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') retry();
    };
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearTimeout(retryTimer.current);
    };
  }, [reloadSession, syncScans]);

  const handleSessionCreated = async () => {
    setShowWelcomePopup(false);

    const sessionId = SessionService.getSessionId();
    if (sessionId) {
      try {
        const newSession = await SessionService.getSession(sessionId);
        setCurrentSession(newSession);

        const pubId = getQueryParam('pubId');
        if (pubId && isValidUUID(pubId)) {
          await recordScan(newSession, pubId);
        }
      } catch (error) {
        console.error('Error fetching new session:', error);
        // The crawl was created; only fetching it failed. Queue the scan
        // against it rather than dropping it: syncing it loads the crawl.
        sessionLoadFailed.current = true;
        const pubId = getQueryParam('pubId');
        if (pubId && isValidUUID(pubId)) {
          await submitScan(sessionId, pubId);
        } else {
          setSyncNotice({ kind: 'error', message: SESSION_LOAD_FAILED, retry: reloadSession });
        }
      }
    }

    setRefreshTrigger(prev => prev + 1);
  };

  const handleCompletionPopupClose = () => {
    setShowCompletionPopup(false);
    setCompletedSession(null);
    setCompletionPopupDismissed(true);
  };

  const handleAbandonCrawl = async () => {
    // Finalise the old session before starting a new one
    const oldSessionId = SessionService.getSessionId();
    if (oldSessionId) {
      // Get any queued scans onto the old crawl before it's finalised; ones
      // that still can't sync are dropped with it
      await flushScanQueue();
      try {
        await SessionService.finishSession(oldSessionId);
      } catch (error) {
        console.error('Error finalising abandoned session:', error);
        if (isRetryableError(error)) {
          // No signal: stay on the old crawl rather than leave it running
          // unsubmitted behind the new one
          setShowAbandonPopup(false);
          setAbandonPopupData(null);
          setSyncNotice({
            kind: 'error',
            message: "Couldn't submit your current crawl, so the new one hasn't started. Check your signal and try again.",
            retry: handleAbandonCrawl,
          });
          return;
        }
        // Already finalised or expired — nothing to do
      }
      ScanQueue.clearSession(oldSessionId);
    }
    setSyncNotice(notice => (notice?.kind === 'error' ? null : notice));
    SessionService.clearSessionId();
    setCurrentSession(null);
    setShowAbandonPopup(false);
    setAbandonPopupData(null);

    const pubId = getQueryParam('pubId');
    if (pubId) {
      await startSessionForPub(pubId);
    }
  };

  const handleCancelAbandon = () => {
    setShowAbandonPopup(false);
    setAbandonPopupData(null);
  };

  /** "Try again" on the no-signal sheet for a first scan. The sheet stays up
   *  until the retry gets somewhere, rather than flickering shut and back. */
  const handleRetryStart = async () => {
    const pubId = getQueryParam('pubId');
    if (pubId && isValidUUID(pubId)) {
      await startSessionForPub(pubId);
    } else {
      setShowStartFailed(false);
    }
  };

  const handleCloseStartFailed = () => setShowStartFailed(false);

  /** "Retry now" on the pending banner, or "Try again" on a refused scan or
   *  another failed step */
  const handleRetrySync = async () => {
    if (syncNotice?.kind === 'error') {
      setSyncNotice(null);
      await syncNotice.retry?.();
      return;
    }
    if (syncNotice?.kind === 'failed') {
      ScanQueue.enqueue(syncNotice.scan);
    }
    setSyncNotice(null);
    retryAttempt.current = 0;
    await syncScans();
  };

  const handleDismissSyncNotice = () => setSyncNotice(null);

  return {
    isLoading,
    currentSession,
    setCurrentSession,
    refreshTrigger,
    showWelcomePopup,
    showCompletionPopup,
    completedSession,
    setCompletedSession,
    showAbandonPopup,
    abandonPopupData,
    handleSessionCreated,
    handleCompletionPopupClose,
    handleAbandonCrawl,
    handleCancelAbandon,
    showStartFailed,
    handleRetryStart,
    handleCloseStartFailed,
    pendingPubIds,
    syncNotice,
    handleRetrySync,
    handleDismissSyncNotice,
  };
};
